import React, { useEffect, useState } from "react";
import { BellRing, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ResultAlerts() {
  const [sub, setSub] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/alerts").then((r) => { setSub(r.data); setEmail(r.data.email || ""); }).catch(() => {});
  }, []);

  const save = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.put("/alerts", { enabled, email, origin_url: window.location.origin });
      setSub(data);
      toast.success(enabled ? "Result alerts on — we'll email you after every draw" : "Result alerts off");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!sub) return null;

  return (
    <div data-testid="result-alerts-card" className="bg-surface border border-oracle/20 rounded-xl p-5 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-oracle/10 border border-oracle/30 flex items-center justify-center shrink-0">
            <BellRing className="w-4 h-4 text-oracle" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-xl text-parchment">Result alerts</div>
            <p className="font-body text-xs text-parchment/55 mt-1 max-w-md">
              After every Mon · Wed · Sat drawing we email your saved picks marked against the winning numbers, with any prize tier you hit.
            </p>
            {sub.guest && (
              <p className="font-body text-[11px] text-parchment/50 mt-1" data-testid="alerts-guest-note">
                {sub.email_source === "checkout" ? "We'll use the email from your checkout — change it below if you like." : "Add the email you'd like alerts sent to."}
              </p>
            )}
            {sub.enabled && sub.verified === false && (
              <p className="font-body text-[11px] text-oracle/90 mt-1" data-testid="alerts-verify-pending">
                Confirmation pending — we sent a link to {sub.email}. Alerts start once you confirm it's yours.
              </p>
            )}
            {sub.last_sent && <p className="font-body text-[11px] text-oracle/80 mt-1" data-testid="alerts-last-sent">Last alert: draw {sub.last_sent.draw_date}</p>}
            {!sub.email_configured && <p className="font-body text-[11px] text-parchment/40 mt-1" data-testid="alerts-email-pending">Email delivery is being activated — your preference is saved and alerts start as soon as it's live.</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="alerts-status" className={`label-caps ${sub.enabled ? "text-oracle" : "text-parchment/40"}`}>{sub.enabled ? "On" : "Off"}</span>
          <Switch data-testid="alerts-toggle" checked={sub.enabled} disabled={saving} onCheckedChange={save} className="data-[state=checked]:bg-oracle" />
        </div>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 sm:max-w-sm">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-parchment/40" strokeWidth={1.5} />
          <Input data-testid="alerts-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for alerts" className="pl-9 bg-black/30 border-white/10 text-parchment font-body focus-visible:ring-oracle" />
        </div>
        <Button data-testid="alerts-save-email-btn" variant="ghost" disabled={saving || !email} onClick={() => save(sub.enabled || true)} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">
          {sub.enabled ? "Update email" : "Turn on with this email"}
        </Button>
      </div>
    </div>
  );
}
